export const GENERATED_COUPON_LOW_WATER = 500;

export const PREFIX_PATTERN = /^[A-Za-z0-9-]{0,20}$/;

export const DEFAULT_DISCOUNT_VALUES = { percentage: 10, fixedAmount: 10, freeShipping: null };

export const defaultDiscount = () => ({
  prefix: '',
  type: 'percentage',
  value: DEFAULT_DISCOUNT_VALUES.percentage,
  appliesTo: { kind: 'order', ids: [] },
  countries: { all: true },
  minimumPurchase: { kind: 'none', value: null },
  combinesWith: { productDiscounts: false, orderDiscounts: false, shippingDiscounts: false },
  activation: { kind: 'atSend', date: null },
  expiration: { kind: 'afterOneYear', days: null, hours: null, date: null },
});

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const isPositiveNumber = (value) => isFiniteNumber(value) && value > 0;

const isNonNegativeInteger = (value) => Number.isInteger(value) && value >= 0;

const isCountryCode = (code) => typeof code === 'string' && /^[A-Z]{2}$/.test(code);

const isValidDate = (value) => typeof value === 'string' && !Number.isNaN(Date.parse(value));

const valueError = (config) => {
  if (config.type === 'percentage') {
    return isPositiveNumber(config.value) && config.value <= 100 ? null : 'Enter a percentage from 0.01 to 100.';
  }
  if (config.type === 'fixedAmount') {
    return isPositiveNumber(config.value) ? null : 'Enter an amount greater than 0.';
  }
  if (config.type === 'freeShipping') {
    return config.value === null || (isFiniteNumber(config.value) && config.value >= 0)
      ? null
      : 'Enter a maximum shipping rate of 0 or more, or leave it empty.';
  }
  return 'Choose a discount type.';
};

const targetError = (config) => {
  if (config.type === 'freeShipping') {
    if (config.countries?.all === false) {
      const countryCodes = config.countries.codes || [];
      if (!countryCodes.length) {
        return 'Select at least one country.';
      }
      if (!countryCodes.every(isCountryCode)) {
        return 'Countries must be two-letter ISO codes.';
      }
    }
    return null;
  }
  if (config.appliesTo?.kind === 'products' && !(config.appliesTo.ids || []).length) {
    return 'Select at least one product.';
  }
  if (config.appliesTo?.kind === 'collections' && !(config.appliesTo.ids || []).length) {
    return 'Select at least one collection.';
  }
  return null;
};

const minimumPurchaseError = ({ minimumPurchase }) => {
  if (!minimumPurchase || minimumPurchase.kind === 'none') {
    return null;
  }
  if (minimumPurchase.kind === 'quantity') {
    return Number.isInteger(minimumPurchase.value) && minimumPurchase.value > 0
      ? null
      : 'Enter a minimum quantity of at least 1.';
  }
  return isPositiveNumber(minimumPurchase.value) ? null : 'Enter a minimum purchase amount greater than 0.';
};

const datesError = ({ activation, expiration }) => {
  if (expiration?.kind === 'afterDuration') {
    const { days, hours } = expiration;
    if (!isNonNegativeInteger(days ?? 0) || !isNonNegativeInteger(hours ?? 0) || !(days > 0 || hours > 0)) {
      return 'Enter a number of days or hours greater than 0.';
    }
  }
  if (expiration?.kind === 'date') {
    if (!isValidDate(expiration.date)) {
      return 'Choose an expiration date.';
    }
    if (activation?.kind === 'date' && Date.parse(expiration.date) <= Date.parse(activation.date)) {
      return 'The expiration date must be after the activation date.';
    }
  }
  return null;
};

const activationError = ({ activation }) =>
  activation?.kind === 'date' && !isValidDate(activation.date) ? 'Choose an activation date.' : null;

export const discountIssue = (config) => {
  if (!config) {
    return { field: 'type', message: 'Configure the discount.' };
  }
  const checks = [
    [
      'prefix',
      () =>
        PREFIX_PATTERN.test(config.prefix || '') ? null : 'Prefixes can use up to 20 letters, numbers and hyphens.',
    ],
    ['value', () => valueError(config)],
    ['targets', () => targetError(config)],
    ['minimumPurchase', () => minimumPurchaseError(config)],
    ['activation', () => activationError(config)],
    ['expiration', () => datesError(config)],
  ];
  for (const [field, check] of checks) {
    const message = check();
    if (message) {
      return { field, message };
    }
  }
  return null;
};

export const discountError = (config) => discountIssue(config)?.message ?? null;

export const generatedCouponStatus = (coupon) => {
  if (coupon.generationError) {
    return 'error';
  }
  if (
    coupon.generationPending > 0 ||
    (coupon.totalCount < GENERATED_COUPON_LOW_WATER && coupon.source === 'generated' && coupon.codesTotal === 0)
  ) {
    return 'generating';
  }
  return 'live';
};
